/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * A directive which displays the connections that the current user has
 * marked as favorites (pinned), rendered as a flat list in the same format
 * used by the "All Connections" list.
 */
angular.module('home').directive('guacFavoriteConnections', [function guacFavoriteConnections() {

    return {
        restrict: 'E',
        replace: true,
        scope: {

            /**
             * The root connection groups to display, and all visible
             * descendants, as a map of data source identifier to the root
             * connection group within that data source. Favorite connections
             * will only be shown if they exist within this hierarchy,
             * regardless of whether they are still marked as favorites.
             *
             * @type Object.<String, ConnectionGroup>
             */
            rootGroups : '=',

            /**
             * Arbitrary object which shall be made available to the
             * connection template within the scope as <code>context</code>.
             * This is used to provide access to the favorite/pinned status
             * of each connection, as well as a means of toggling that
             * status.
             *
             * @type Object
             */
            context : '='

        },

        templateUrl: 'app/home/templates/guacFavoriteConnections.html',
        controller: ['$scope', '$injector', function guacFavoriteConnectionsController($scope, $injector) {

            // Required types
            var ClientIdentifier = $injector.get('ClientIdentifier');
            var GroupListItem    = $injector.get('GroupListItem');

            // Required services
            var guacFavorites = $injector.get('guacFavorites');

            /**
             * Map of data source identifier to a synthetic root
             * GroupListItem whose children are the favorite connections
             * within that data source. Passed directly to guacGroupList for
             * display using the same list format as the "All Connections"
             * list.
             *
             * @type Object.<String, GroupListItem>
             */
            $scope.favoriteConnectionGroups = {};

            /**
             * Returns whether any favorite connections are available for
             * display.
             *
             * @returns {!boolean}
             *     true if favorite connections are present, false otherwise.
             */
            $scope.hasFavoriteConnections = function hasFavoriteConnections() {
                return _.some($scope.favoriteConnectionGroups,
                    function hasChildren(rootItem) {
                        return !!rootItem.children.length;
                    }
                );
            };

            /**
             * Map of client identifier to the data source and connection
             * associated with that identifier, for all connections currently
             * visible to the user.
             *
             * @type Object.<String, {dataSource: String, connection: Connection}>
             */
            var visibleConnections = {};

            /**
             * Adds the given connection to the internal set of visible
             * connections.
             *
             * @param {String} dataSource
             *     The identifier of the data source associated with the
             *     given connection.
             *
             * @param {Connection} connection
             *     The connection to add to the internal set of visible
             *     connections.
             */
            var addVisibleConnection = function addVisibleConnection(dataSource, connection) {

                var identifier = ClientIdentifier.toString({
                    dataSource : dataSource,
                    type       : ClientIdentifier.Types.CONNECTION,
                    id         : connection.identifier
                });

                visibleConnections[identifier] = {
                    dataSource : dataSource,
                    connection : connection
                };

            };

            /**
             * Walks the given connection group and all descendants, adding
             * all contained connections to the internal set of visible
             * connections.
             *
             * @param {String} dataSource
             *     The identifier of the data source associated with the
             *     given connection group.
             *
             * @param {ConnectionGroup} connectionGroup
             *     The connection group to walk.
             */
            var addVisibleConnectionGroup = function addVisibleConnectionGroup(dataSource, connectionGroup) {

                if (connectionGroup.childConnections)
                    connectionGroup.childConnections.forEach(function addChildConnection(child) {
                        addVisibleConnection(dataSource, child);
                    });

                if (connectionGroup.childConnectionGroups)
                    connectionGroup.childConnectionGroups.forEach(function addChildConnectionGroup(child) {
                        addVisibleConnectionGroup(dataSource, child);
                    });

            };

            /**
             * Rebuilds the map of favorite connection groups based on the
             * current set of visible connections and the identifiers
             * currently marked as favorites.
             */
            var updateFavoriteConnectionGroups = function updateFavoriteConnectionGroups() {

                var childrenByDataSource = {};

                guacFavorites.favorites.forEach(function addFavorite(identifier) {

                    var visible = visibleConnections[identifier];
                    if (!visible)
                        return;

                    if (!childrenByDataSource[visible.dataSource])
                        childrenByDataSource[visible.dataSource] = [];

                    childrenByDataSource[visible.dataSource].push(
                        GroupListItem.fromConnection(visible.dataSource, visible.connection, false));

                });

                var favoriteConnectionGroups = {};
                angular.forEach($scope.rootGroups, function addRoot(rootGroup, dataSource) {
                    favoriteConnectionGroups[dataSource] = new GroupListItem({
                        children : childrenByDataSource[dataSource] || []
                    });
                });

                $scope.favoriteConnectionGroups = favoriteConnectionGroups;

            };

            // Update visible connections when root groups are set
            $scope.$watch('rootGroups', function setRootGroups(rootGroups) {

                visibleConnections = {};
                if (rootGroups) {
                    angular.forEach(rootGroups, function addConnectionGroup(rootGroup, dataSource) {
                        addVisibleConnectionGroup(dataSource, rootGroup);
                    });
                }

                updateFavoriteConnectionGroups();

            });

            // Update the displayed favorites whenever the underlying set of
            // favorites changes (such as when a connection is pinned or
            // unpinned)
            $scope.$watchCollection(function getFavorites() {
                return guacFavorites.favorites;
            }, updateFavoriteConnectionGroups);

        }]

    };
}]);
